import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {DropDownsModule, SharedModule} from '@progress/kendo-angular-dropdowns';
import {ErrorComponent, FormFieldComponent, NumericTextBoxComponent} from '@progress/kendo-angular-inputs';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {LabelComponent} from '@progress/kendo-angular-label';
import {PriorityService} from '../../../../../../common/jira/services/priority.service';
import {Priority} from '../../../../../../common/jira/models/priority.model';
import {DatePipe, NgForOf, NgIf} from '@angular/common';
import {EpicService} from '../../../../../../common/jira/services/epic.service';
import {Epic} from '../../../../../../common/jira/models/epic.model';
import {CustomGridModule} from '../../../custom-grid/custom-grid.module';
import {ToolBarButtonComponent, ToolBarButtonGroupComponent, ToolBarComponent} from '@progress/kendo-angular-toolbar';
import {KENDO_EDITOR} from '@progress/kendo-angular-editor';
import {TranslateModule} from '@ngx-translate/core';
import {WindowModule} from '@progress/kendo-angular-dialog';
import {IssueTypeService} from '../../../../../../common/jira/services/issue-type.service';
import {IssueTypeModel} from '../../../../../../common/jira/models/issue-type.model';
import {IssueService} from '../../../../../../common/jira/services/issue.service';
import {TaskTypeService} from "../../../../../../common/jira/services/task-type.service";
import {TaskType} from "../../../../../../common/jira/models/task-type.model";
import {map} from 'rxjs/operators';
import {firstValueFrom, Observable} from 'rxjs';
import {LoaderComponent} from '@progress/kendo-angular-indicators';
import {User} from '../../../../../../common/user-auth';
import {UserAuth} from '../../../../../../common';
import {NgxDropzoneChangeEvent, NgxDropzoneModule} from 'ngx-dropzone';
import {JiraSyncContext} from '../../../../../../common/jira/models/issue.model';
import {DescriptionEntry} from '../../../../../../common/jira/models/create-issue.model';
import {NotificationService} from '@progress/kendo-angular-notification';

@Component({
  selector: 'app-ticket-jira-sync-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DropDownsModule,
    SharedModule,
    NumericTextBoxComponent,
    LabelComponent,
    NgIf,
    FormFieldComponent,
    ErrorComponent,
    CustomGridModule,
    NgForOf,
    ToolBarComponent,
    ToolBarButtonComponent,
    ToolBarButtonGroupComponent,
    DatePipe,
    TranslateModule,
    KENDO_EDITOR,
    WindowModule,
    LoaderComponent,
    NgxDropzoneModule,
  ],
  templateUrl: './ticket-jira-sync-modal.component.html',
  styleUrls: ['./ticket-jira-sync-modal.component.css']
})

export class TicketJiraSyncModalComponent implements OnInit {
  userAuth: User = UserAuth.get();
  priorities: Priority[] = [];
  epics: Epic[] = [];
  filteredEpics: Epic[] = [];
  issueType: IssueTypeModel[] = [];
  taskTypes: TaskType[] = [];
  showInfoLogs = false;
  replyPhotoOpen = false;
  photoReplyView = null;
  isCreating = false;
  jiraAdditionalAttachment: string | null = null;

  @Input({required: true}) context!: JiraSyncContext;
  @Output() onSubmitToParent = new EventEmitter<void>();

  form = new FormGroup({
    storyPoints: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(1),
      Validators.max(100)
    ]),
    priority: new FormControl<Priority | null>(null, Validators.required),
    issueType: new FormControl<IssueTypeModel | null>(null, Validators.required),
    epic: new FormControl<Epic | null>(null, Validators.required),
    jiraAdditionalDescription: new FormControl<string>(''),
    taskType: new FormControl<TaskType[]>([], Validators.required),
  });

  constructor(
    private epicService: EpicService,
    private priorityService: PriorityService,
    private issueTypeService: IssueTypeService,
    private issueService: IssueService,
    private toast: NotificationService,
    private taskTypeService: TaskTypeService,
  ) {
  }

  onEpicFilter(value: string): void {
    this.filteredEpics = this.epics.filter(epic =>
      epic.summary.toLowerCase().includes(value.toLowerCase())
    );
  }

  ngOnInit(): void {
    this.loadPriorities();
    this.loadEpics();
    this.loadIssueTypes();
    this.loadTaskTypes();
    this.filteredEpics = [...this.epics]
  }

  getReporterId(): Observable<string> {
    return this.issueService.getIssueById(this.form.value.epic.id).pipe(
      map(issue => issue.data.fields.reporter.accountId)
    );
  }

  async onSubmit() {
    this.form.markAllAsTouched();
    if (!this.form.valid) return;

    this.isCreating = true;
    const payload = await this.buildPayload();

    this.toast.show({type: {style: 'info', icon: true}, content: 'Jira issue is being created...'});
    this.onSubmitToParent.emit();

    this.issueService.createIssue(payload).subscribe({
      error: () => this.toast.show({type: {style: 'error', icon: true}, content: 'Failed during Jira issue creation'}),
      next: () => this.toast.show({type: {style: 'success', icon: true}, content: 'Jira issue created successfully!'}),
    });
  }

  private async buildPayload() {
    const descriptionEntries = this.buildDescriptionEntries();

    return {
      ticket_id: this.context.ticket.id,
      reporter_id: await firstValueFrom(this.getReporterId()),
      summary: this.context.ticket.subject,
      description_entries: descriptionEntries,
      issuetype_id: this.form.value.issueType.id,
      priority_id: this.form.value.priority?.id,
      parent_key: this.form.value.epic?.key,
      story_points: this.form.value.storyPoints,
      task_type: this.form.value.taskType.map(e => e.value),
    };
  }

  private buildDescriptionEntries() {
    const entries: DescriptionEntry[] = [];

    const initialTicket = this.parseHtmlToContentBlocks(this.context.ticket.description);

    this.context.ticket.files?.forEach(f => {
      initialTicket.push({type: 'image_url' as const, value: `${this.context.fileUrl}/${f.fileName}`});
    });

    entries.push({
      author: `${this.context.ticket.user.name} ${this.context.ticket.user.surname}`,
      content_blocks: initialTicket,
    });

    this.context.ticketReplies?.forEach(reply => {
      const replyBlocks = this.parseHtmlToContentBlocks(reply.description);
      reply.files?.forEach(f => {
        replyBlocks.push({type: 'image_url' as const, value: `${this.context.fileUrl}/${f.fileName}`});
      });
      entries.push({
        author: reply.user,
        content_blocks: replyBlocks,
      });
    });

    const userBlocks = this.parseHtmlToContentBlocks(this.form.value.jiraAdditionalDescription);
    if (this.jiraAdditionalAttachment) {
      userBlocks.push({type: 'image_url' as const, value: this.jiraAdditionalAttachment});
    }
    entries.push({
      author: this.userAuth.fullName,
      content_blocks: userBlocks,
    });

    return entries;
  }

  private parseHtmlToContentBlocks(html: string) {
    if (!html) return [];

    const blocks = [];
    const div = document.createElement('div');
    div.innerHTML = html;
    let currentText = '';

    const flushText = () => {
      const trimmed = currentText.trim();
      if (trimmed) {
        blocks.push({type: 'text', value: trimmed});
      }
      currentText = '';
    };

    const walk = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        currentText += node.textContent || '';
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        if (el.tagName === 'IMG') {
          flushText();
          const src = el.getAttribute('src');
          if (src) {
            blocks.push({type: 'image_url', value: src});
          }
        } else if (el.tagName === 'BR') {
          currentText += '\n';
        } else {
          const isBlock = /^(P|DIV|H[1-6]|LI|UL|OL|BLOCKQUOTE|TABLE|TR)$/.test(el.tagName);
          if (isBlock && currentText.trim()) {
            flushText();
          }
          el.childNodes.forEach(child => walk(child));
          if (isBlock) {
            flushText();
          }
        }
      }
    };

    div.childNodes.forEach(child => walk(child));
    flushText();

    return blocks;
  }

  private loadPriorities() {
    this.priorityService.getPriorities().subscribe(res => {
      this.priorities = res.data;
    });
  }

  private loadEpics() {
    this.epicService.getEpics().subscribe(res => {
      this.epics = res.data.values;
      this.filteredEpics = [...this.epics];
    })
  }

  private loadIssueTypes() {
    this.issueTypeService.getIssueTypes().subscribe(res => {
      this.issueType = res.data
    })
  }

  private loadTaskTypes() {
    this.taskTypeService.getTaskTypes().subscribe(res => {
      this.taskTypes = res.data
    })
  }

  onFilesAdded(event: NgxDropzoneChangeEvent): void {
    const file = event.addedFiles[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      this.jiraAdditionalAttachment = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  openReplyPhoto(url) {
    if (url.includes('.pdf')) {
      window.open(`${this.context.fileUrl}/${url}`, '_blank');
    } else {
      this.replyPhotoOpen = true;
      this.photoReplyView = `${this.context.fileUrl}/${url}`;
    }
  }

  closeReplyPhoto() {
    this.replyPhotoOpen = false;
    this.photoReplyView = null;
  }
}
